import { useProvidedProgramContext } from '@/hooks/use-provided-program-context';
import { DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import commonMessages from '@rhg/lang/messages/common';
import {
    Box,
    Fade,
    Stack,
    SxProps,
    Theme,
    Typography,
    useMediaQuery,
    useTheme,
} from '@rhg/libs/material';
import { useIntl } from 'react-intl';
import SessionRating from '../session-rating';

const styleSpacer = { mr: 14 };
const styleNarrartorSection = { mb: DS_SPACING_UNITS.SECTION };

interface AudioPlayerContentProps {
    title?: string;
    description?: string;
    narrators?: string[];
    authors?: string[];
    sx?: SxProps<Theme>;
    areControlsHidden?: boolean;
}
export function AudioPlayerContent({
    title,
    description,
    narrators,
    authors,
    sx,
    areControlsHidden,
}: AudioPlayerContentProps) {
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const programContext = useProvidedProgramContext();

    const isClinicalProgram =
        programContext?.programType === 'clinical_program';

    return (
        <Fade in={!areControlsHidden} mountOnEnter unmountOnExit>
            <Stack direction="column" alignItems="center" sx={sx}>
                {title && (
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        component="h1"
                        color="text.primary"
                        textAlign="center"
                        mb={DS_SPACING_UNITS.SECTION}
                    >
                        {title}
                    </Typography>
                )}
                {description && (
                    <Typography
                        variant="body1"
                        color="text.body"
                        textAlign="center"
                        mb={DS_SPACING_UNITS.SECTION}
                    >
                        {description}
                    </Typography>
                )}
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={styleNarrartorSection}
                >
                    {narrators && narrators.length > 0 && (
                        <Box>
                            <Typography
                                variant="body1"
                                color="text.sub"
                                mb={4}
                                textAlign="center"
                            >
                                {formatMessage(
                                    commonMessages['common.narrator']
                                ).toLocaleUpperCase()}
                            </Typography>
                            {narrators?.map((narrator, i) => (
                                <Typography
                                    variant="subtitle2"
                                    color="text.body"
                                    textAlign="center"
                                    component="div"
                                    key={i}
                                >
                                    {narrator}
                                    {i < narrators.length - 1 ? ', ' : ''}
                                </Typography>
                            ))}
                        </Box>
                    )}
                    {narrators &&
                        narrators.length > 0 &&
                        authors &&
                        authors.length > 0 && <Box sx={styleSpacer}></Box>}
                    {authors && authors.length > 0 && (
                        <Box>
                            <Typography
                                variant="body1"
                                color="text.sub"
                                mb={4}
                                textAlign="center"
                            >
                                {formatMessage(
                                    commonMessages['common.author']
                                ).toLocaleUpperCase()}
                            </Typography>
                            {authors?.map((author, i) => (
                                <Typography
                                    variant="subtitle2"
                                    color="text.body"
                                    textAlign="center"
                                    component="div"
                                    key={i}
                                >
                                    {author}
                                    {i < authors.length - 1 ? ', ' : ''}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Stack>
                {isClinicalProgram && <SessionRating />}
            </Stack>
        </Fade>
    );
}
